<template>
  <section
      ref="heroRef"
      class="archive-hero"
      :style="dynamicStyle"
  >
    <div class="archive-hero__inner" :class="{'archive-hero__inner--image': image, 'archive-hero__inner--no-image': !image}">
      <h1
          class="archive-hero__title"
          v-html="title"
      />
      <time v-if="publishTime"
            :datetime="publishTime"
            class="archive-hero__time">
        {{ computedDate }}
      </time>
    </div>
    <figure
        v-if="image"
        class="archive-hero__figure"
    >
      <AppImageFocal :data="computedFocalImage" :renditions="renditions" :eager="true"/>
    </figure>
  </section>
</template>

<script setup lang="ts">

import {computed, onMounted, reactive, ref, watch} from 'vue'
import {useWindowScroll} from '~/composables/useWindowScroll'
import {useDate} from "~/composables/useDate";
import {useImageAsFocalImage} from "~/composables/useImageAsFocalImage";

const heroRef = ref<HTMLElement>()

const props = defineProps<{
  image: unknown,
  title: String,
  publishTime?: String
}>()

const state = reactive({
  scroll: 0,
  publishTime: undefined
})

// support for images that do not have a focus-point
const computedFocalImage = useImageAsFocalImage(props.image)

const computedDate = computed(() => {
  return props.publishTime ? useDate(props.publishTime).value : undefined
})

const renditions = [
  {width: 575, height: 323, breakpoint: 'xs'},
  {width: 991, height: 557, breakpoint: 'sm'},
]

const dynamicStyle = computed(() => {
  const height = heroRef.value?.getBoundingClientRect().height | 0
  const opacity = (state.scroll) ? (height - state.scroll) / height : 1
  return {opacity: (opacity < 0) ? 0 : (opacity > 1) ? 1 : opacity}
})

onMounted(() => {
  // watch for scroll
  const scrollState = useWindowScroll()
  watch(scrollState, (newState) => {
    state.scroll = newState.scroll
  })
})

</script>

<style scoped lang="postcss">
.archive-hero {
  @mixin container-xl;
  position: sticky;
  top: 0px;
  z-index: -10;
  padding-top: 4rem;
  opacity: 0;

  @media (--md) {
    padding-top: 5rem;
  }

  @media (--lg) {
    column-gap: 1.5rem;
    padding-top: 6rem;
  }

  @media (--xl) {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    column-gap: 1.75rem;
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }

  @media (--xxl) {
    column-gap: 2rem;
    padding-left: 2rem;
    padding-right: 2rem;
  }

  &__inner {
    grid-column: 1 / -1;

    &--image {
      margin-bottom: 2rem;
    }

    &--no-image {
      margin-bottom: 1.75rem;
    }

    @media (--xl) {
      &--no-image {
        margin-bottom: 1rem;
      }
    }
  }

  &__title {
    hyphens: auto;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    text-align: center;
    font-family: Midnight, sans-serif;
    font-size: 28px;
    text-transform: uppercase;
    line-height: 0.9;
    @mixin hyphenation-specs;

    @media (--sm) {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    @media (--md) {
      padding-left: 1.25rem;
      padding-right: 1.25rem;
      font-size: 56px;
    }

    @media (--lg) {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }

    @media (--xl) {
      padding-left: 0px;
      padding-right: 0px;
      line-height: 0.8;
    }
  }

  &__time {
    grid-column: 1 / -1;
    margin-top: 0.25rem;
    display: block;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    text-align: center;
    @mixin heading-h5-wide;

    @media (--sm) {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    @media (--md) {
      padding-left: 1.25rem;
      padding-right: 1.25rem;
    }

    @media (--lg) {
      margin-top: 0.5rem;
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }

    @media (--xl) {
      padding-left: 0px;
      padding-right: 0px;
    }

    @media (--xxl) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }
  }

  &__figure {
    grid-column: 1 / -1;
    margin-bottom: 1.5rem;

    @media (--lg) {
      margin-bottom: 2.5rem;
    }

    @media (--xl) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }

    @media (--xxl) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }
  }
}
</style>
